import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Stack,
  useTheme,
  Grid,
  Paper,
  Button,
} from "@mui/material";
import useOpenRequest from "../../hooks/useOpenRequest";
import { API_ENDPOINT } from "../../utils/constants";
import MuPb from "../widgets/MuPb";

const VisitorProfileView = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();

  // Verify visitor profile token
  const {
    data: verifyData,
    isLoading: isVerifying,
    isError: isVerifyError,
    error: verifyError,
  } = useOpenRequest(
    `${API_ENDPOINT}user/verify-visitor-profile-token?id=${id}`
  );

  // Fetch visitor profile details upon successful verification
  const {
    data: profileData,
    isLoading: isLoadingProfile,
    isError: isProfileError,
    error: profileError,
  } = useQuery(
    ["visitorProfile", id],
    async () => {
      const response = await fetch(
        `${API_ENDPOINT}user/get-visitor-profile-token?id=${id}`
      );
      if (!response.ok) throw new Error("Failed to fetch profile details");
      return response.json();
    },
    { enabled: !!verifyData }
  );

  if (isVerifying) return <MuPb />;
  if (isVerifyError)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error" sx={{ width: "100%", maxWidth: 400 }}>
          {verifyError?.message}
        </Alert>
      </Box>
    );
  if (isLoadingProfile)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (isProfileError)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error" sx={{ width: "100%", maxWidth: 400 }}>
          {profileError?.message}
        </Alert>
      </Box>
    );

  return (
    <Box display="flex" justifyContent="center" my={4} px={2}>
      <Card
        sx={{
          maxWidth: 500,
          width: "100%",
          boxShadow: 3,
          borderRadius: 4,
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",

            height: 150, // Fixed height for 3:1 banner

            mt: -1,
          }}
        >
          {profileData?.settings?.bnLogo && (
            <Box
              component="img"
              src={profileData.settings.bnLogo}
              alt={`${profileData?.company} banner`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px 8px 0 0",
                boxShadow: 2,
              }}
            />
          )}
          {profileData?.settings?.srLogo && (
            <Avatar
              src={profileData.settings.srLogo}
              alt={`${profileData?.company} logo`}
              sx={{
                width: 120,
                height: 120,
                position: "absolute",
                bottom: -60, // Half of the avatar hangs below the banner
                left: "50%",
                transform: "translateX(-50%)",
                border: "4px solid white",
                bgcolor: "grey.100",
                boxShadow: 3,
              }}
            />
          )}
        </Box>

        <CardContent sx={{ position: "relative" }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                navigate(`/visitor-request/${id}`);
              }}
              sx={{
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                padding: "10px 16px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                mt: 10,
                mb: 2,
              }}
            >
              📅 Request a Meeting with {profileData.company}
            </Button>
            <Avatar
              src={profileData.profileImage}
              alt={profileData.name}
              sx={{
                width: 100,
                height: 100,
                border: `4px solid ${theme.palette.primary.main}`,
              }}
            />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {profileData.name}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              gutterBottom
              sx={{ maxWidth: "80%", textAlign: "center" }}
            >
              {profileData.email}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Country
              </Typography>
              <Typography variant="body1">
                {profileData.country.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                State
              </Typography>
              <Typography variant="body1">{profileData.state.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                City
              </Typography>
              <Typography variant="body1">{profileData.city.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Barcode ID
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {profileData.barcodeId}
              </Typography>
            </Grid>
          </Grid>

          {/* <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[100],
            }}
          >
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              QR Code
            </Typography>
            <Box display="flex" justifyContent="center">
              <img
                src={profileData.qrLink}
                alt="Visitor QR Code"
                style={{ width: 150, height: 150 }}
              />
            </Box>
          </Paper> */}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[100],
            }}
          >
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Aadhar Card
            </Typography>
            <Box display="flex" justifyContent="center">
              <img
                src={profileData.aadharUrl}
                alt="Aadhar Document"
                style={{
                  width: "100%",
                  maxWidth: 400,
                  height: "auto",
                  borderRadius: theme.shape.borderRadius,
                }}
              />
            </Box>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VisitorProfileView;
